import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ICourse } from "@/api/admin/course";
import type { IPageAuthor } from "@/types/author";

const globalSlice = createSlice({
  name: "global",
  initialState: {
    isExistingLead: false,
    lead_submitted_successfully: false,
    isPromotionalBannerOpen: false,
    pageCategory: "",
    // Page-level master analytics switch. Read by rudderStackEvents.sendEvent —
    // false suppresses ALL tracking for the page. Default true (matches server).
    trackingEnabled: true,
    // Page-level chatbot switch. Read by PageScript — false suppresses the chatbot
    // for the current page. Default true (matches the server column) so routes that
    // never set it keep whatever the university-level setting gives them.
    chatbotEnabled: true,
    promotional_banner: {
      desktop_image: "",
      mobile_image: "",
      enabled: false,
      animationColor: "",
      promotionalIcon: "",
      /** Quiet gap between two blinks of the floating icon, in seconds. null → 2s. */
      blinkIntervalSeconds: null as number | string | null,
    },
    university: {
      name: "",
      source: "",
      uni_code: "",
      chatbot_provider: "",
    },
    course: {} as ICourse,
    // Author linked to the current page (server: page.author). NULL when the page
    // has no author or the author is unpublished. Any widget can read this instead
    // of fetching the author itself.
    pageAuthor: null as IPageAuthor | null,
    floatingIcons: {
      whatsApp: {
        whatsAppLink: "",
        whatsAppIcon: "",
      },
      phone: {
        phoneLink: "",
        phoneIcon: "",
      },
    },
    floatingIconsVisibility: {
      isWhatsAppIconEnabled: false,
      isPhoneIconEnabled: false,
      isPromotionalBannerEnabled: false,
      // Floating promo icon: on unless a page explicitly turns it off. Only ever
      // visible when isPromotionalBannerEnabled is also true.
      isPromotionalFloatingIconEnabled: true,
      // Blink of that icon: on unless a page explicitly turns it off. Independent of
      // visibility above — off means "icon shown, but static".
      isPromotionalIconBlinkEnabled: true,
    },
  },
  reducers: {
    setLeadDetailsAction: (
      state,
      action: PayloadAction<{ isExistingLead: boolean }>,
    ) => {
      state.isExistingLead = action.payload.isExistingLead;
    },
    setIsLeadSubmittedSuccessfully: (state, action: PayloadAction<boolean>) => {
      state.lead_submitted_successfully = action.payload;
    },
    setUniversity: (
      state,
      action: PayloadAction<{
        /** university.name — the per-tenant analytics label (see customAnalytics/rudderStackEvents). */
        name?: string;
        source: string;
        uni_code: string;
        chatbot_provider?: string;
      }>
    ) => {
      state.university = {
        name: action.payload.name || "",
        source: action.payload.source,
        uni_code: action.payload.uni_code,
        chatbot_provider: action.payload.chatbot_provider || "",
      };
    },

    setPromotionalBanner: (
      state,
      action: PayloadAction<{
        desktop_image: string;
        mobile_image: string;
        enabled: boolean;
        animationColor: string;
        promotionalIcon: string;
        blinkIntervalSeconds?: number | string | null;
      }>,
    ) => {
      state.promotional_banner = {
        ...action.payload,
        // Keep the key present even if a caller omits it — OfferBox treats
        // null/invalid as "use the 2s default".
        blinkIntervalSeconds: action.payload.blinkIntervalSeconds ?? null,
      };
    },
    setPageCategory: (state, action: PayloadAction<string>) => {
      state.pageCategory = action.payload;
    },
    setTrackingEnabled: (state, action: PayloadAction<boolean>) => {
      state.trackingEnabled = action.payload;
    },
    setChatbotEnabled: (state, action: PayloadAction<boolean>) => {
      state.chatbotEnabled = action.payload;
    },
    setCourseLeadDropData: (state, action: PayloadAction<ICourse>) => {
      state.course = action.payload;
    },
    setPageAuthor: (state, action: PayloadAction<IPageAuthor | null>) => {
      state.pageAuthor = action.payload;
    },
    setPromotionalBannerOpen: (state, action: PayloadAction<boolean>) => {
      state.isPromotionalBannerOpen = action.payload;
    },
    setFloatingIcons: (
      state,
      action: PayloadAction<{
        whatsApp: {
          whatsAppLink: string;
          whatsAppIcon: string;
        };
        phone: {
          phoneLink: string;
          phoneIcon: string;
        };
      }>,
    ) => {
      state.floatingIcons = action.payload;
    },
    setFloatingIconsVisibility: (
      state,
      action: PayloadAction<{
        isWhatsAppIconEnabled: boolean;
        isPhoneIconEnabled: boolean;
        isPromotionalBannerEnabled: boolean;
        isPromotionalFloatingIconEnabled: boolean;
        isPromotionalIconBlinkEnabled: boolean;
      }>,
    ) => {
      state.floatingIconsVisibility = action.payload;
    },
  },
});

export const {
  setLeadDetailsAction,
  setIsLeadSubmittedSuccessfully,
  setUniversity,
  setPageCategory,
  setTrackingEnabled,
  setChatbotEnabled,
  setCourseLeadDropData,
  setPageAuthor,
  setPromotionalBanner,
  setPromotionalBannerOpen,
  setFloatingIcons,
  setFloatingIconsVisibility,
} = globalSlice.actions;

export default globalSlice.reducer;
